/**
 * Nutrition math used by the UI. Every constant carries its source.
 *
 * - BMR: Mifflin MD, St Jeor ST, et al. A new predictive equation for resting
 *   energy expenditure in healthy individuals. Am J Clin Nutr. 1990;51(2):241-247.
 *   PMID 2305711. The most accurate of the common equations in nonobese and obese adults:
 *   Frankenfield D, Roth-Yousey L, Compher C. J Am Diet Assoc. 2005;105(5):775-789. PMID 15883556.
 * - Physical activity level (PAL) multipliers: FAO/WHO/UNU. Human energy
 *   requirements. Report of a Joint Expert Consultation, Rome 2001 (published
 *   2004). Lifestyle bands: sedentary/light 1.40–1.69, active/moderate 1.70–1.99,
 *   vigorous 2.00–2.40. We use points inside those bands.
 * - Fiber adequate intake: 14 g per 1,000 kcal. Institute of Medicine. Dietary
 *   Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids,
 *   Cholesterol, Protein, and Amino Acids. National Academies Press, 2005.
 * - Sodium (chronic-disease risk reduction intake, adults: reduce if above
 *   2,300 mg/day) and potassium adequate intake (3,400 mg men / 2,600 mg women):
 *   NASEM. Dietary Reference Intakes for Sodium and Potassium. 2019.
 * - Energy per gram (Atwater general factors): protein 4, carbohydrate 4,
 *   fat 9, alcohol 7 kcal/g.
 */
import type { ActivityLevel, MealNutrients, Profile, Sex } from './types';

export const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9, alcohol: 7 } as const;

export const PAL: Readonly<Record<ActivityLevel, number>> = {
  sedentary: 1.4,
  light: 1.55,
  moderate: 1.7,
  very_active: 1.9,
  extra_active: 2.2,
};

export const ACTIVITY_LABEL: Readonly<Record<ActivityLevel, string>> = {
  sedentary: 'Sedentary: desk job, mostly sitting, little exercise',
  light: 'Lightly active: desk job plus regular walking or 1–3 workouts/week',
  moderate: 'Moderately active: on your feet most of the day, or ~1 h training daily',
  very_active: 'Very active: physical job plus regular training',
  extra_active: 'Vigorous: heavy manual labor or athlete-level training volume',
};

/** Mifflin–St Jeor resting energy expenditure, kcal/day. */
export function mifflinStJeorBmr(weightKg: number, heightCm: number, ageYears: number, sex: Sex): number {
  const base: number = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
  return sex === 'male' ? base + 5 : base - 161;
}

/**
 * Resting energy from lean body mass, kcal/day: 500 + 22 x LBM (kg). Cunningham JJ. A reanalysis of
 * the factors influencing basal metabolic rate in normal adults. Am J Clin Nutr. 1980;33(11):2372-4.
 * PMID 7435418.
 * Weight-only equations run low for very muscular people, whose extra weight is metabolically
 * active tissue. Among the established equations this one was the most accurate in recreational
 * athletes (ten Haaf T, Weijs PJM. PLoS One. 2014;9(9):e108460, PMID 25275434) and had the
 * smallest bias in physique athletes, where Mifflin-St Jeor ran about 15% low (Tinsley GM et al.
 * Appl Physiol Nutr Metab. 2019;44(4):397-406, PMID 30240568).
 * Not the lifting app's resting term (Cunningham 1991, 370 + 21.6 x FFM, in Lifting's Physiology.kt).
 */
export function cunninghamBmr(leanMassKg: number): number {
  return 500 + 22 * leanMassKg;
}

export interface EnergyEstimate {
  bmrKcal: number;
  pal: number;
  tdeeKcal: number;
  activityKcal: number;
}

export function estimateEnergy(weightKg: number, heightCm: number, ageYears: number, sex: Sex, activity: ActivityLevel): EnergyEstimate {
  const bmrKcal: number = mifflinStJeorBmr(weightKg, heightCm, ageYears, sex);
  const pal: number = PAL[activity];
  const tdeeKcal: number = bmrKcal * pal;
  return { bmrKcal, pal, tdeeKcal, activityKcal: tdeeKcal - bmrKcal };
}

/** Available ("net") carbohydrate. carbs_g is total carbohydrate, US-label style. */
export function netCarbs(carbsG: number, fiberG: number | null | undefined): number {
  return Math.max(0, carbsG - (fiberG ?? 0));
}

export function sumNutrients(rows: ReadonlyArray<MealNutrients>): MealNutrients {
  const acc: MealNutrients = {
    calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0,
    fiber_g: 0, sugar_g: 0, alcohol_g: 0, sodium_mg: 0, potassium_mg: 0,
  };
  for (const r of rows) {
    acc.calories += r.calories;
    acc.protein_g += r.protein_g;
    acc.carbs_g += r.carbs_g;
    acc.fat_g += r.fat_g;
    acc.fiber_g = (acc.fiber_g ?? 0) + (r.fiber_g ?? 0);
    acc.sugar_g = (acc.sugar_g ?? 0) + (r.sugar_g ?? 0);
    acc.alcohol_g = (acc.alcohol_g ?? 0) + (r.alcohol_g ?? 0);
    acc.sodium_mg = (acc.sodium_mg ?? 0) + (r.sodium_mg ?? 0);
    acc.potassium_mg = (acc.potassium_mg ?? 0) + (r.potassium_mg ?? 0);
  }
  return acc;
}

/** Share of calories from each macro (Atwater), 0–1. Returns zeros for an empty day. */
export function macroEnergySplit(n: MealNutrients): { protein: number; carbs: number; fat: number; alcohol: number } {
  const p: number = n.protein_g * KCAL_PER_G.protein;
  const c: number = n.carbs_g * KCAL_PER_G.carbs;
  const f: number = n.fat_g * KCAL_PER_G.fat;
  const a: number = (n.alcohol_g ?? 0) * KCAL_PER_G.alcohol;
  const total: number = p + c + f + a;
  if (total <= 0) return { protein: 0, carbs: 0, fat: 0, alcohol: 0 };
  return { protein: p / total, carbs: c / total, fat: f / total, alcohol: a / total };
}

export interface NutrientTarget {
  key: 'fiber_g' | 'sodium_mg' | 'potassium_mg';
  label: string;
  unit: string;
  target: number;
  /** "min" = aim to reach it; "max" = stay under it. */
  kind: 'min' | 'max';
  source: string;
}

export function nutrientTargets(profile: Pick<Profile, 'sex'>, calorieTarget: number | null): NutrientTarget[] {
  const kcal: number = calorieTarget ?? 2000;
  return [
    { key: 'fiber_g', label: 'Fiber', unit: 'g', target: Math.round((kcal / 1000) * 14), kind: 'min', source: 'IOM 2005: 14 g per 1,000 kcal' },
    { key: 'potassium_mg', label: 'Potassium', unit: 'mg', target: profile.sex === 'female' ? 2600 : 3400, kind: 'min', source: 'NASEM 2019 adequate intake' },
    { key: 'sodium_mg', label: 'Sodium', unit: 'mg', target: 2300, kind: 'max', source: 'NASEM 2019: reduce if above 2,300 mg' },
  ];
}

/** Calorie target to use: explicit profile target, else null. */
export function calorieTarget(profile: Profile | null): number | null {
  return profile?.target_calories ?? null;
}
